/**
 * Leadler › Harita as a scan dashboard (v0.1.79). Sercan, 30 Sept 2026: "harita çizimi kötü,
 * hiç anlaşılır değil: hangi ülke taranmış, kaç yer bulunmuş anlamıyorum; bulunanları
 * bulduğumuz detaylarla listelememiz lazım". Top to bottom: the world map (the region picker),
 * six summary tiles for the chosen region (cells scanned, queries by source, companies by
 * source, qualified, cost, pace), one sentence that says where the scan stands, the region map
 * fitted to the scanned cells with their scan order and town names (layers can be switched),
 * the cell card, the plan, the firm table (name, suburb, source, fit, stage) with search and
 * filters, the firm card, the partial cells and the queue. Company-level data only.
 */

import { type ReactNode, useEffect, useMemo, useState } from 'react';
import {
  CELL_CLASS_LABELS,
  CELL_STATUS_LABELS,
  CELL_STATUSES,
  LEAD_STAGE_LABELS,
  type LeadListItem,
  type LeadMapCell,
  type LeadsView,
  PROVIDER_LABELS,
  type ScanCurve,
  type SourceStat,
  type WorldRegion,
} from '../api/leads.js';
import { usePanel } from '../app/context.js';
import { useLeads } from '../app/leads.js';
import { formatNumber, formatPct, formatRelativeDay, formatUsd } from '../lib/format.js';
import { COVERAGE_MIN_COMPANIES, SaturationCurve, SourceOverlap } from './leads-charts.js';
import {
  byPlanOrder,
  CellCard,
  countBy,
  DAY_MS,
  FIT_FILTERS,
  FirmCard,
  type FitFilter,
  LIST_PAGE,
  MIN_QUERIES_PER_CELL,
  RegionPanel,
  SOURCE_LABELS,
  STAGE_FILTERS,
  sum,
} from './leads-map.js';
import { SectionHeader } from './primitives.js';
import { cellOfCompany, RegionMap } from './region-map.js';
import { ChoiceChips } from './Sheet.js';
import { ScanPlan, WorldMap } from './world-map.js';

const QUALIFIED_STAGES = new Set<string>(['nitelikli', 'dogrulandi', 'temas', 'cevap', 'musteri']);
/** How many queued cells the focused map shows next to the scanned ones. */
const QUEUE_ON_MAP = 6;
const MAP_SCOPES = ['Taranan bölge', 'Bütün bölge'] as const;
type MapScope = (typeof MAP_SCOPES)[number];
/** Playback of the scan order: one cell per step. */
const PLAY_STEP_MS = 450;

/** Scanned cells numbered in the order the engine reached them (first run, else last run, else plan). */
export function scanOrderOf(cells: readonly LeadMapCell[]): Map<string, number> {
  const at = (cell: LeadMapCell) => cell.firstScannedAt ?? cell.lastScannedAt ?? Number.MAX_SAFE_INTEGER;
  const scanned = cells
    .filter((cell) => cell.status !== 'taranmadi')
    .sort((a, b) => at(a) - at(b) || (a.plannedOrder ?? 0) - (b.plannedOrder ?? 0) || a.cellPk - b.cellPk);
  return new Map(scanned.map((cell, index) => [cell.cellId, index + 1]));
}

/** The first suburb of each cell, in the given order, without repeats. */
function townsOf(cells: readonly LeadMapCell[], max = 3): string[] {
  const seen = new Set<string>();
  const towns: string[] = [];
  for (const cell of cells) {
    const town = cell.localities[0];
    if (town === undefined || seen.has(town)) continue;
    seen.add(town);
    towns.push(town);
    if (towns.length === max) break;
  }
  return towns;
}

function providerLine(sources: readonly SourceStat[]): string {
  const sorted = [...sources].sort((a, b) => b.queries - a.queries);
  if (sorted.length === 0) return 'kaynak dökümü yok';
  return sorted
    .slice(0, 4)
    .map((source) => `${PROVIDER_LABELS[source.provider] ?? source.provider} ${formatNumber(source.queries)}`)
    .join(' · ');
}

function sourceLine(companies: readonly LeadListItem[]): string {
  const counts = new Map<string, number>();
  for (const company of companies) {
    const label = SOURCE_LABELS[company.source] ?? company.source;
    counts.set(label, (counts.get(label) ?? 0) + 1);
  }
  const sorted = [...counts.entries()].sort((a, b) => b[1] - a[1]);
  if (sorted.length === 0) return 'henüz firma yok';
  return sorted
    .slice(0, 3)
    .map(([label, n]) => `${label} ${formatNumber(n)}`)
    .join(' · ');
}

function Tile({
  label,
  value,
  unit,
  sub,
  children,
}: {
  label: string;
  value: string;
  unit?: string | undefined;
  sub: string;
  children?: ReactNode;
}) {
  return (
    <li className="kpi">
      <span className="kpi__label">{label}</span>
      <span className="kpi__value">
        <span className="kpi__num mono">{value}</span>
        {unit === undefined ? null : <span className="kpi__unit">{unit}</span>}
      </span>
      {children}
      <span className="kpi__sub">{sub}</span>
    </li>
  );
}

/** The six numbers Sercan asked for, for one region: cells, queries, companies, qualified, cost, pace. */
function ScanSummary({
  code,
  region,
  cells,
  companies,
  sources,
  curve,
  order,
  weeklyCellQuota,
  now,
}: {
  code: string;
  region: WorldRegion | null;
  cells: LeadMapCell[];
  companies: LeadListItem[];
  sources: SourceStat[];
  curve: ScanCurve | null;
  order: Map<string, number>;
  weeklyCellQuota: number;
  now: number;
}) {
  const total = cells.length;
  const done = countBy(cells, 'tamamlandi');
  const partial = countBy(cells, 'kismi');
  const scanning = countBy(cells, 'taraniyor');
  const waiting = countBy(cells, 'taranmadi');
  const scanned = done + partial + scanning;
  const queries = sum(cells, 'queriesRun');
  const results = cells.reduce((acc, cell) => acc + cell.resultsSeen, 0);
  // The list and the cell counters agree on the live server; a view without the list (the mock) keeps the counters.
  const found = Math.max(companies.length, sum(cells, 'leads'));
  const qualified = Math.max(
    companies.filter((company) => QUALIFIED_STAGES.has(company.stage)).length,
    sum(cells, 'qualifiedLeads'),
  );
  const unfit = companies.filter((company) => company.stage === 'uygun_degil').length;
  const open = Math.max(0, found - qualified - unfit);
  const cost = cells.reduce((acc, cell) => acc + cell.costMicroUsd, 0);
  const perCell = scanned > 0 ? Math.round(cost / scanned) : null;
  const perCompany = found > 0 ? Math.round(cost / found) : null;
  const last7 = cells.filter((cell) => cell.lastScannedAt !== null && now - cell.lastScannedAt <= 7 * DAY_MS).length;
  const remaining = waiting + partial;
  const etaWeeks = weeklyCellQuota > 0 ? Math.ceil(remaining / weeklyCellQuota) : null;
  const ordered = [...order.entries()]
    .sort((a, b) => a[1] - b[1])
    .flatMap(([cellId]) => cells.filter((cell) => cell.cellId === cellId));
  const towns = townsOf(ordered);
  const name = region?.name ?? code;
  const sentence =
    scanned === 0
      ? `${name}: henüz tarama yok; ${formatNumber(total)} hücre sırada.`
      : `${name}: ${towns.length > 0 ? `${towns.join(', ')} çevresinde ` : ''}${scanned} hücre tarandı (${done} tam, ${partial} kısmi${scanning > 0 ? `, ${scanning} taranıyor` : ''}), ${formatNumber(found)} firma bulundu, ${formatNumber(qualified)} nitelikli; ${formatNumber(waiting)} hücre sırada.`;
  const barLabel = `${name} tarama ilerlemesi: ${total} hücrenin ${scanned} tarandı`;
  return (
    <>
      <ul className="kpi-grid" aria-label="Tarama özeti">
        <Tile
          label="Taranan hücre"
          value={formatNumber(scanned)}
          unit={`/ ${formatNumber(total)}`}
          sub={`${done} tam · ${partial} kısmi${scanning > 0 ? ` · ${scanning} taranıyor` : ''} · ${formatNumber(waiting)} sırada`}
        >
          <div className="rp__bar" role="img" aria-label={barLabel}>
            {CELL_STATUSES.map((status) => {
              const n = countBy(cells, status);
              return n === 0 || total === 0 ? null : (
                <span
                  key={status}
                  className={`rp__seg rp__seg--${status}`}
                  style={{ width: `${(100 * n) / total}%` }}
                />
              );
            })}
          </div>
        </Tile>
        <Tile
          label="Sorgu"
          value={formatNumber(queries)}
          unit={`· ${formatNumber(results)} sonuç`}
          sub={providerLine(sources)}
        />
        <Tile
          label="Bulunan firma"
          value={formatNumber(found)}
          sub={
            companies.length === 0 && found > 0
              ? 'hücre sayaçlarından; kaynak dökümü listeyle gelir'
              : sourceLine(companies)
          }
        />
        <Tile
          label="Nitelikli"
          value={formatNumber(qualified)}
          unit={found > 0 ? formatPct(qualified / found) : undefined}
          sub={`${formatNumber(open)} incelenecek · ${formatNumber(unfit)} uygun değil`}
        />
        <Tile
          label="Maliyet"
          value={formatUsd(cost, 2)}
          sub={
            perCell === null
              ? 'henüz harcama yok'
              : `hücre başına ${formatUsd(perCell, 2)}${perCompany === null ? '' : ` · firma başına ${formatUsd(perCompany, 3)}`}`
          }
        />
        <Tile
          label="Tahmini kapsama"
          value={curve !== null && curve.observed >= COVERAGE_MIN_COMPANIES ? formatPct(curve.coverage) : 'yok'}
          unit={
            curve !== null && curve.observed >= COVERAGE_MIN_COMPANIES
              ? `· ~${formatNumber(Math.round(curve.estimate))} firma`
              : undefined
          }
          sub={
            curve === null || curve.observed < COVERAGE_MIN_COMPANIES
              ? `Chao1 tahmini için en az ${COVERAGE_MIN_COMPANIES} çözümlenmiş firma gerekir`
              : `Chao1: ${formatNumber(curve.observed)} görüldü, ${curve.f1} tek sorguda, ${curve.f2} iki sorguda`
          }
        />
        <Tile
          label="Hız"
          value={formatNumber(last7)}
          unit="hücre / 7 gün"
          sub={
            etaWeeks === null
              ? 'kota yok'
              : remaining === 0
                ? 'bölge bitti'
                : `haftada ${weeklyCellQuota} ile ~${etaWeeks} hafta (${formatNumber(remaining)} hücre)`
          }
        />
      </ul>
      <p className="lr-summary">{sentence}</p>
    </>
  );
}

function Toggle({ label, on, onChange }: { label: string; on: boolean; onChange: (value: boolean) => void }) {
  return (
    <label className="lr-toggle">
      <input type="checkbox" checked={on} onChange={(event) => onChange(event.target.checked)} />
      {label}
    </label>
  );
}

export function LeadMap({ view }: { view: LeadsView }) {
  const { now } = usePanel();
  const { store, state } = useLeads();
  const firstWithCells = view.world.regions.filter((item) => item.cells > 0).sort(byPlanOrder)[0]?.code;
  const [region, setRegion] = useState(firstWithCells ?? view.regions[0] ?? '');
  const regionInfo = view.world.regions.find((item) => item.code === region) ?? null;
  const [stageFilter, setStageFilter] = useState('Tümü');
  const [fitFilter, setFitFilter] = useState<FitFilter>('Tümü');
  const [sourceFilter, setSourceFilter] = useState('Tümü');
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(LIST_PAGE);
  const [scope, setScope] = useState<MapScope>('Taranan bölge');
  const [showOrder, setShowOrder] = useState(true);
  const [showTowns, setShowTowns] = useState(true);
  const [showCompanies, setShowCompanies] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [playhead, setPlayhead] = useState<number | null>(null);
  const [showWater, setShowWater] = useState(true);
  const [showRoads, setShowRoads] = useState(true);
  const [showIndustrial, setShowIndustrial] = useState(true);

  const cells = useMemo(() => view.cells.filter((cell) => cell.region === region), [view.cells, region]);
  const cellIds = useMemo(() => new Set(cells.map((cell) => cell.cellId)), [cells]);
  const companies = view.companies.filter(
    (company) => company.ownerRegion === region || cellOfCompany(company, cellIds) !== null,
  );
  const sources = view.sources.filter((source) => source.region === region);
  const curve = view.curves.find((item) => item.region === region) ?? null;
  const order = useMemo(() => scanOrderOf(cells), [cells]);
  useEffect(() => {
    if (!playing) return;
    const timer = setInterval(() => setPlayhead((prev) => (prev ?? 0) + 1), PLAY_STEP_MS);
    return () => clearInterval(timer);
  }, [playing]);
  useEffect(() => {
    if (playing && playhead !== null && playhead >= order.size) {
      setPlaying(false);
      setPlayhead(null);
    }
  }, [playing, playhead, order.size]);
  useEffect(() => {
    if (region !== '') void store.loadBasemap(region);
  }, [store, region]);
  const basemap = state.basemaps[region] ?? null;
  const togglePlay = () => {
    if (playing) {
      setPlaying(false);
      setPlayhead(null);
      return;
    }
    setPlayhead(0);
    setPlaying(true);
  };
  const focus = useMemo(() => {
    if (scope !== 'Taranan bölge' || order.size === 0) return null;
    const queued = cells
      .filter((cell) => cell.status === 'taranmadi' && cell.plannedOrder !== null)
      .sort((a, b) => (a.plannedOrder ?? 0) - (b.plannedOrder ?? 0))
      .slice(0, QUEUE_ON_MAP);
    return [...cells.filter((cell) => order.has(cell.cellId)), ...queued];
  }, [cells, order, scope]);

  const selectedCellId = state.selectedCellId;
  const cellFilter = selectedCellId !== null && cellIds.has(selectedCellId) ? selectedCellId : null;
  const selectedCell = cellFilter === null ? null : (cells.find((cell) => cell.cellId === cellFilter) ?? null);
  const stages = STAGE_FILTERS.find((filter) => filter.label === stageFilter)?.stages ?? null;
  const fitMin = FIT_FILTERS.find((filter) => filter.label === fitFilter)?.min ?? 0;
  const sourceLabel = (company: LeadListItem) => SOURCE_LABELS[company.source] ?? company.source;
  const providerText = (company: LeadListItem) =>
    company.providers.length > 0
      ? company.providers.map((provider) => PROVIDER_LABELS[provider] ?? provider).join(' + ')
      : sourceLabel(company);
  const sourceOptions = ['Tümü', ...new Set(companies.map(sourceLabel))];
  const stageLabels = Object.fromEntries(
    STAGE_FILTERS.map((filter) => [
      filter.label,
      `${filter.label} ${
        filter.stages === null
          ? companies.length
          : companies.filter((company) => filter.stages?.includes(company.stage)).length
      }`,
    ]),
  );
  const needle = query.trim().toLocaleLowerCase('tr-TR');
  const listed = companies.filter(
    (company) =>
      (stages === null || stages.includes(company.stage)) &&
      company.fit.total >= fitMin &&
      (sourceFilter === 'Tümü' || sourceLabel(company) === sourceFilter) &&
      (cellFilter === null || cellOfCompany(company, cellIds) === cellFilter) &&
      (needle === '' ||
        [company.name, company.locality ?? '', company.domain ?? '', company.cellId ?? '', company.h3r6 ?? ''].some(
          (text) => text.toLocaleLowerCase('tr-TR').includes(needle),
        )),
  );
  const pins: LeadListItem[] = companies.filter(
    (company) => company.lat !== null && company.lon !== null && company.stage !== 'uygun_degil',
  );
  const selectedId = state.selectedLeadId;
  const select = (leadId: string) => {
    void store.selectLead(selectedId === leadId ? null : leadId);
  };
  const toggleCell = (cellId: string) => {
    void store.selectCell(cellFilter === cellId ? null : cellId);
    setLimit(LIST_PAGE);
  };
  const resetList = () => setLimit(LIST_PAGE);

  const summary = `${region}: ${cells.length} hücre, ${CELL_STATUSES.map(
    (status) => `${countBy(cells, status)} ${CELL_STATUS_LABELS[status].toLocaleLowerCase('tr-TR')}`,
  ).join(', ')}, ${sum(cells, 'qualifiedLeads')} nitelikli lead, ${pins.length} firma iğnesi`;
  const queue = cells
    .filter((cell) => cell.plannedOrder !== null)
    .sort((a, b) => (a.plannedOrder ?? 0) - (b.plannedOrder ?? 0))
    .slice(0, 8);
  const partial = cells
    .filter((cell) => cell.status === 'kismi')
    .map((cell) => ({ cell, missing: Math.max(0, MIN_QUERIES_PER_CELL - cell.queriesRun) }))
    .sort((a, b) => a.missing - b.missing || b.cell.qualifiedLeads - a.cell.qualifiedLeads)
    .slice(0, 10);

  const regionOptions = [
    ...new Set([...view.world.regions.filter((item) => item.cells > 0).map((item) => item.code), ...view.regions]),
  ];
  const regionChip = (code: string) => {
    const info = view.world.regions.find((item) => item.code === code);
    if (info === undefined) return code;
    const scanned = info.scanned + info.partial + info.scanning;
    return scanned > 0 ? `${code} · ${scanned} tarandı` : code;
  };
  return (
    <>
      <WorldMap view={view} selected={region} onSelect={setRegion} />
      <section className="lr-block lr-block--wide" aria-labelledby="lr-ozet">
        <SectionHeader
          id="lr-ozet"
          title="Tarama özeti"
          right={
            regionInfo ? (
              <span className="section-header__aside">
                {regionInfo.name} · dalga {regionInfo.wave} · {formatRelativeDay(view.asOf, now)}
              </span>
            ) : undefined
          }
        />
        <ChoiceChips
          label="Bölge"
          options={regionOptions}
          value={region}
          onChange={(value) => {
            setRegion(value);
            resetList();
          }}
          labels={Object.fromEntries(regionOptions.map((code) => [code, regionChip(code)]))}
        />
        {cells.length === 0 && regionInfo ? (
          <p className="lr-empty-region">
            {regionInfo.name} için hücre ızgarası henüz üretilmedi (dalga {regionInfo.wave}, öncelik{' '}
            {regionInfo.priority}
            ). Sıra gelince hücreler burada görünür; ülkeler: {regionInfo.countries.join(', ')}.
          </p>
        ) : (
          <ScanSummary
            code={region}
            region={regionInfo}
            cells={cells}
            companies={companies}
            sources={sources}
            curve={curve}
            order={order}
            weeklyCellQuota={view.world.weeklyCellQuota}
            now={now}
          />
        )}
      </section>

      <section className="lr-block lr-block--wide" aria-labelledby="lr-harita">
        <SectionHeader
          id="lr-harita"
          title="Tarama haritası"
          right={
            order.size > 0 ? (
              <span className="section-header__aside">numaralar tarama sırası · adlar hücrenin banliyösü</span>
            ) : undefined
          }
        />
        <div className="lr-toggles">
          <ChoiceChips<MapScope> label="Görünüm" options={[...MAP_SCOPES]} value={scope} onChange={setScope} />
          <Toggle label="Sıra numaraları" on={showOrder} onChange={setShowOrder} />
          <Toggle label="Yer adları" on={showTowns} onChange={setShowTowns} />
          <Toggle label="Firmalar" on={showCompanies} onChange={setShowCompanies} />
          <Toggle label="Su ve kıyı" on={showWater} onChange={setShowWater} />
          <Toggle label="Yollar" on={showRoads} onChange={setShowRoads} />
          <Toggle label="Sanayi alanları" on={showIndustrial} onChange={setShowIndustrial} />
          {order.size > 1 ? (
            <span className="lr-play">
              <button type="button" className="btn btn--secondary" onClick={togglePlay}>
                {playing ? 'Durdur' : 'Tarama sırasını oynat'}
              </button>
              {playing ? (
                <span className="mono ink-2" aria-live="polite">
                  {' '}
                  {playhead ?? 0} / {order.size}
                </span>
              ) : null}
            </span>
          ) : null}
        </div>
        {cells.length > 0 ? (
          <RegionMap
            key={`${region}:${scope}`}
            cells={cells}
            companies={pins}
            selectedCellId={cellFilter}
            selectedLeadId={selectedId}
            onSelectCell={toggleCell}
            onSelectLead={select}
            summary={summary}
            focus={focus}
            order={showOrder ? order : undefined}
            showTowns={showTowns}
            showCompanies={showCompanies}
            dimUnscanned={scope === 'Taranan bölge'}
            route={showOrder}
            playhead={playhead}
            layers={basemap}
            showWater={showWater}
            showRoads={showRoads}
            showIndustrial={showIndustrial}
          />
        ) : (
          <p className="placeholder-text">Bu bölgede hücre yok.</p>
        )}
        {basemap !== null && basemap.layers.length === 0 ? (
          <p className="lr-note ink-2">
            Harita altlığı (kıyı, su, ana yollar, sanayi alanları) bölgenin ilk tarama koşusundan sonra gelir.
          </p>
        ) : null}
        <ul className="lr-legend" aria-label="Hücre durumları">
          {CELL_STATUSES.map((status) => (
            <li key={status} className="lr-legend__item">
              <span className={`rm-swatch rm-swatch--${status}`} aria-hidden="true" />
              {CELL_STATUS_LABELS[status]} <span className="mono">{countBy(cells, status)}</span>
            </li>
          ))}
          <li className="lr-legend__item">
            <span className="rm-swatch rm-swatch--pin-qualified" aria-hidden="true" />
            Nitelikli firma
          </li>
          <li className="lr-legend__item">
            <span className="rm-swatch rm-swatch--pin" aria-hidden="true" />
            Bulundu
          </li>
          <li className="lr-legend__item">
            <span className="rm-swatch rm-swatch--route" aria-hidden="true" />
            Tarama rotası
          </li>
          <li className="lr-legend__item">
            <span className="rm-swatch rm-swatch--water" aria-hidden="true" />
            Su ve kıyı
          </li>
          <li className="lr-legend__item">
            <span className="rm-swatch rm-swatch--road" aria-hidden="true" />
            Ana yol
          </li>
          <li className="lr-legend__item">
            <span className="rm-swatch rm-swatch--industrial" aria-hidden="true" />
            Sanayi alanı (OSM)
          </li>
          <li className="lr-legend__item ink-2">
            "Taranan bölge" haritayı taranan hücrelere ve sıradaki {QUEUE_ON_MAP} hücreye sığdırır, taranmamışlar soluk
            kalır · balon: hücredeki firma sayısı, yakınlaşınca iğnelere açılır · hücreye dokununca kartı açılır ve
            liste süzülür
          </li>
        </ul>
        <RegionPanel cells={cells} region={regionInfo} weeklyCellQuota={view.world.weeklyCellQuota} now={now} />
      </section>
      {cells.length > 0 ? (
        <section className="lr-block lr-block--wide" aria-labelledby="lr-doygunluk">
          <SectionHeader id="lr-doygunluk" title="Doygunluk ve kaynaklar" />
          <div className="lr-charts">
            <SaturationCurve curve={curve} />
            <SourceOverlap companies={companies} sources={sources} />
          </div>
        </section>
      ) : null}
      {selectedCell !== null ? (
        <section className="lr-block lr-block--wide" aria-labelledby="lr-hucre">
          <SectionHeader id="lr-hucre" title="Seçili hücre" />
          <CellCard
            cell={selectedCell}
            detail={state.selectedCell}
            loading={state.selectedCellLoading}
            error={state.selectedCellError}
            now={now}
            onClose={() => void store.selectCell(null)}
          />
        </section>
      ) : null}
      <ScanPlan view={view} now={now} onSelect={setRegion} />

      <section className="lr-block" aria-labelledby="lr-firmalar">
        <SectionHeader id="lr-firmalar" title="Firmalar" count={listed.length} />
        <div className="lr-firms__tools">
          <label className="lr-search">
            <span className="sr-only">Firma ara</span>
            <input
              type="search"
              value={query}
              placeholder="Firma, banliyö, alan adı veya hücre ara"
              onChange={(event) => {
                setQuery(event.target.value);
                resetList();
              }}
            />
          </label>
          <ChoiceChips
            label="Aşama"
            options={STAGE_FILTERS.map((filter) => filter.label)}
            value={stageFilter}
            onChange={(value) => {
              setStageFilter(value);
              resetList();
            }}
            labels={stageLabels}
          />
          <ChoiceChips<FitFilter>
            label="Uyum puanı"
            options={FIT_FILTERS.map((filter) => filter.label)}
            value={fitFilter}
            onChange={(value) => {
              setFitFilter(value);
              resetList();
            }}
          />
          {sourceOptions.length > 2 ? (
            <ChoiceChips
              label="Kaynak"
              options={sourceOptions}
              value={sourceFilter}
              onChange={(value) => {
                setSourceFilter(value);
                resetList();
              }}
            />
          ) : null}
          {cellFilter === null ? null : (
            <button type="button" className="lr-chip-clear" onClick={() => void store.selectCell(null)}>
              Hücre <span className="mono">{cellFilter}</span> · süzgeci kaldır
            </button>
          )}
        </div>
        {listed.length === 0 ? (
          <p className="placeholder-text">Bu süzgeçte firma yok.</p>
        ) : (
          <>
            <div className="lr-firms__head" aria-hidden="true">
              <span />
              <span>Firma</span>
              <span>Banliyö · hücre</span>
              <span>Kaynak</span>
              <span>Uyum</span>
              <span>Aşama</span>
            </div>
            <ol className="lr-firms" aria-label="Firma listesi">
              {listed.slice(0, limit).map((company) => {
                const on = company.leadId === selectedId;
                const cellOf = cellOfCompany(company, cellIds) ?? company.cellId;
                return (
                  <li key={company.leadId}>
                    <button
                      type="button"
                      className={on ? 'lr-firm lr-firm--on' : 'lr-firm'}
                      aria-pressed={on}
                      aria-label={`${company.name}, ${company.locality ?? 'konum yok'}${cellOf === null ? '' : `, hücre ${cellOf}`}, kaynak ${providerText(company)}, uyum ${company.fit.total}, ${LEAD_STAGE_LABELS[company.stage]}`}
                      onClick={() => select(company.leadId)}
                    >
                      <span className={`lr-firm__dot lr-firm__dot--${company.stage}`} aria-hidden="true" />
                      <span className="lr-firm__name">
                        {company.name}
                        {company.domain === null ? null : <span className="lr-firm__domain">{company.domain}</span>}
                      </span>
                      <span className="lr-firm__where">
                        {company.locality ?? 'konum yok'}
                        {cellOf === null ? null : <span className="mono lr-firm__cell"> {cellOf}</span>}
                      </span>
                      <span className="lr-firm__src">{providerText(company)}</span>
                      <span className="lr-firm__fit">
                        <span className="lr-firm__track" aria-hidden="true">
                          <span className="lr-firm__bar" style={{ width: `${Math.min(100, company.fit.total)}%` }} />
                        </span>
                        <span className="mono">{company.fit.total}</span>
                      </span>
                      <span className="lr-firm__stage">{LEAD_STAGE_LABELS[company.stage]}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </>
        )}
        {listed.length > limit ? (
          <button type="button" className="lr-more" onClick={() => setLimit(limit + LIST_PAGE)}>
            Daha fazla göster ({listed.length - limit})
          </button>
        ) : null}
      </section>

      <section className="lr-block" aria-labelledby="lr-firma">
        <SectionHeader id="lr-firma" title="Seçili firma" />
        <FirmCard detail={state.selectedLead} loading={state.selectedLoading} error={state.selectedError} now={now} />
      </section>

      <section className="lr-block lr-block--wide" aria-labelledby="lr-kismi">
        <SectionHeader id="lr-kismi" title="Önce bitirilecek kısmi hücreler" count={partial.length} />
        <p className="ink-2 lr-note">
          Doygunluk kuralı hücre başına en az {MIN_QUERIES_PER_CELL} sorgu ister; bu hücreler yarım kaldı.
        </p>
        {partial.length === 0 ? (
          <p className="placeholder-text">Yarım kalmış hücre yok.</p>
        ) : (
          // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must be reachable by keyboard.
          <section className="lr-table-wrap" aria-label="Kısmi hücreler tablosu" tabIndex={0}>
            <table className="lr-table">
              <thead>
                <tr>
                  <th scope="col" className="num">
                    Sıra
                  </th>
                  <th scope="col">Hücre</th>
                  <th scope="col">Banliyöler</th>
                  <th scope="col" className="num">
                    Sorgu
                  </th>
                  <th scope="col" className="num">
                    Eksik
                  </th>
                  <th scope="col" className="num">
                    Firma
                  </th>
                  <th scope="col" className="num">
                    Nitelikli
                  </th>
                  <th scope="col">Son tarama</th>
                </tr>
              </thead>
              <tbody>
                {partial.map(({ cell, missing }) => (
                  <tr key={cell.cellPk}>
                    <td className="num mono">{order.get(cell.cellId) ?? ''}</td>
                    <th scope="row" className="mono">
                      {cell.cellId}
                    </th>
                    <td>
                      {cell.localities.slice(0, 3).join(', ')}
                      {cell.localities.length > 3 ? ` +${cell.localities.length - 3}` : ''}
                    </td>
                    <td className="num mono">{cell.queriesRun}</td>
                    <td className="num mono">{missing}</td>
                    <td className="num mono">{cell.leads}</td>
                    <td className="num mono">{cell.qualifiedLeads}</td>
                    <td className="mono">
                      {cell.lastScannedAt === null ? 'yok' : formatRelativeDay(cell.lastScannedAt, now)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </section>

      <section className="lr-block lr-block--wide" aria-labelledby="lr-sira">
        <SectionHeader id="lr-sira" title="Sıradaki hücreler" count={queue.length} />
        {queue.length === 0 ? (
          <p className="placeholder-text">Bu bölgede planlı hücre yok.</p>
        ) : (
          // biome-ignore lint/a11y/noNoninteractiveTabindex: a scrollable region must be reachable by keyboard.
          <section className="lr-table-wrap" aria-label="Sıradaki hücreler tablosu" tabIndex={0}>
            <table className="lr-table">
              <thead>
                <tr>
                  <th scope="col" className="num">
                    Sıra
                  </th>
                  <th scope="col">Hücre</th>
                  <th scope="col">Durum</th>
                  <th scope="col">Tür</th>
                  <th scope="col" className="num">
                    Sorgu
                  </th>
                  <th scope="col" className="num">
                    Nitelikli
                  </th>
                  <th scope="col">Sonraki tarama</th>
                </tr>
              </thead>
              <tbody>
                {queue.map((cell) => (
                  <tr key={cell.cellPk}>
                    <td className="num mono">{cell.plannedOrder}</td>
                    <th scope="row">
                      <span className="mono">{cell.cellId}</span>
                      {cell.localities[0] === undefined ? null : (
                        <span className="lr-table__sub"> {cell.localities[0]}</span>
                      )}
                    </th>
                    <td>{CELL_STATUS_LABELS[cell.status]}</td>
                    <td>{CELL_CLASS_LABELS[cell.cellClass]}</td>
                    <td className="num mono">{cell.queriesRun}</td>
                    <td className="num mono">{cell.qualifiedLeads}</td>
                    <td className="mono">
                      {cell.nextScanAt === null ? 'yok' : formatRelativeDay(cell.nextScanAt, now)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </section>
    </>
  );
}
